import type { Topic } from "./types";

export const WORD_ORDER: Topic = {
  slug: "woordvolgorde",
  title: "Woordvolgorde",
  tagline: "A2 sentence word order — the V2 rule, sub-clauses, and the verb-final trap.",
  duration: "14 min read",
  summary:
    "Why the verb is always second in a Dutch main clause, why it sails to the end in a sub-clause, and how to keep them straight under exam pressure.",
  relevantDeelen: [1, 2, 3, 4],
  relevantCriteria: ["gr"],
  sections: [
    { kind: "heading", level: 2, text: "Why word order matters" },
    {
      kind: "paragraph",
      text: "Dutch word order is the single most-tested A2 grammar item. The DUO Spreken rubric flags wrong word order under Grammatica even when the rest of the sentence is fine. The good news: there are only three patterns to remember. Once you internalise them, word order stops being something you 'think about' and becomes automatic.",
    },
    {
      kind: "callout",
      tone: "rule",
      title: "The three patterns",
      text: "1) Main clause = verb in second position (V2). 2) Yes/no question = verb first. 3) Sub-clause = verb at the end.",
    },

    { kind: "heading", level: 2, text: "Pattern 1 — Main clause: V2" },
    {
      kind: "paragraph",
      text: "In a normal Dutch statement, the conjugated verb is ALWAYS in the second position. Not the second word — the second SLOT. A slot can hold one word or a whole phrase.",
    },
    {
      kind: "table",
      headers: ["Slot 1 (anything)", "Slot 2 (VERB)", "Rest"],
      emphasizeFirstCol: false,
      rows: [
        ["Ik", "ga", "morgen naar school."],
        ["Morgen", "ga", "ik naar school."],
        ["Naar school", "ga", "ik morgen."],
        ["Met de fiets", "ga", "ik morgen naar school."],
      ],
    },
    {
      kind: "paragraph",
      text: "All four sentences mean roughly the same thing. What changes is the emphasis (what the speaker puts in slot 1). What does NOT change is the verb in slot 2. Move 'ik' anywhere — the verb stays put.",
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Common A2 mistake",
      text: "When you start a sentence with anything other than the subject, students often forget to flip. *Morgen ik ga naar school.* is wrong. The Dutch ear hears it instantly. The fix: as soon as you say something before the subject, the verb comes NEXT.",
    },

    { kind: "heading", level: 3, text: "Two-verb main clauses" },
    {
      kind: "paragraph",
      text: "When a main clause has two verbs (a modal or auxiliary plus an infinitive or past participle), the conjugated one stays in slot 2 and the other one goes to the end.",
    },
    {
      kind: "table",
      headers: ["Slot 1", "Verb (slot 2)", "Middle", "Verb at end"],
      rows: [
        ["Ik", "wil", "morgen naar Amsterdam", "gaan."],
        ["Hij", "heeft", "een boek", "gekocht."],
        ["We", "kunnen", "de bus om acht uur", "nemen."],
        ["Zij", "is", "naar de dokter", "geweest."],
      ],
    },
    {
      kind: "callout",
      tone: "tip",
      title: "Memory aid",
      text: "Two-verb sentences make a 'sandwich': conjugated verb early, second verb late, everything else in the middle.",
    },

    { kind: "heading", level: 2, text: "Pattern 2 — Yes/no questions" },
    {
      kind: "paragraph",
      text: "If you can answer the question with ja or nee, the verb comes FIRST. The subject moves to slot 2.",
    },
    {
      kind: "table",
      headers: ["Verb (slot 1)", "Subject (slot 2)", "Rest"],
      rows: [
        ["Ga", "je", "morgen naar school?"],
        ["Heb", "je", "een fiets?"],
        ["Komt", "hij", "uit Turkije?"],
        ["Wil", "u", "koffie?"],
      ],
    },
    { kind: "heading", level: 3, text: "WH-questions" },
    {
      kind: "paragraph",
      text: "Questions that start with a question word (wie, wat, waar, wanneer, hoe, waarom) follow the V2 rule like a normal main clause. The question word is slot 1, the verb is slot 2.",
    },
    {
      kind: "examples",
      rows: [
        { dutch: "Waar woon je?", english: "Where do you live?" },
        { dutch: "Wanneer kom je thuis?", english: "When do you come home?" },
        { dutch: "Hoe gaat het?", english: "How is it going?" },
        { dutch: "Waarom werk je niet?", english: "Why don't you work?" },
      ],
    },

    { kind: "heading", level: 2, text: "Pattern 3 — Sub-clauses (verb at the end)" },
    {
      kind: "paragraph",
      text: "A sub-clause is a piece of sentence introduced by a connector like omdat, dat, als, wanneer, terwijl, voordat, nadat, hoewel, of (yes/no), or a relative pronoun (die, dat). In a sub-clause, the verb sails ALL THE WAY to the end.",
    },
    {
      kind: "table",
      headers: ["Connector", "Subject", "Middle", "Verb at end"],
      rows: [
        ["…omdat", "ik", "honger", "heb."],
        ["…dat", "hij", "morgen", "komt."],
        ["…als", "je", "tijd", "hebt."],
        ["…voordat", "we", "naar huis", "gaan."],
        ["…hoewel", "het", "regent,", "fietsen we."],
      ],
    },
    {
      kind: "callout",
      tone: "rule",
      title: "Two-verb sub-clauses",
      text: "If the sub-clause has two verbs, BOTH go to the end. Order is usually auxiliary then infinitive: …omdat ik moet werken. …als hij is gekomen.",
    },

    { kind: "heading", level: 3, text: "Sub-clause inside a main clause" },
    {
      kind: "paragraph",
      text: "A common A2 sentence has BOTH a main clause and a sub-clause connected by omdat or want. Only one of them gets verb-final treatment — the omdat clause. After 'want' the order stays normal V2.",
    },
    {
      kind: "examples",
      rows: [
        { dutch: "Ik blijf thuis omdat ik ziek ben.", english: "I stay home because I am sick. (verb 'ben' at the end)" },
        { dutch: "Ik blijf thuis want ik ben ziek.", english: "Same meaning, but 'want' keeps normal V2." },
        { dutch: "Ze leert Nederlands omdat ze in Nederland werkt.", english: "She learns Dutch because she works in the Netherlands." },
      ],
    },
    {
      kind: "callout",
      tone: "warning",
      title: "Trap",
      text: "*Ik blijf thuis omdat ik ben ziek.* — wrong. After omdat the verb MUST go to the end. Drill this aloud: omdat-ik-iets-iets-VERB.",
    },

    { kind: "heading", level: 2, text: "Negation — niet vs geen" },
    {
      kind: "paragraph",
      text: "Word order also affects where 'niet' goes. The rule of thumb: niet goes near the END of what it negates. Geen replaces 'een' for indefinite nouns.",
    },
    {
      kind: "examples",
      rows: [
        { dutch: "Ik heb geen auto.", english: "I don't have a car. (geen replaces een)" },
        { dutch: "Ik werk niet.", english: "I don't work. (niet at the end)" },
        { dutch: "Ik werk vandaag niet.", english: "I'm not working today." },
        { dutch: "Ik ga niet naar school.", english: "I'm not going to school." },
        { dutch: "De bus komt niet.", english: "The bus isn't coming." },
      ],
    },

    { kind: "heading", level: 2, text: "Time–Manner–Place" },
    {
      kind: "paragraph",
      text: "When a sentence has multiple adverbs in the middle, Dutch prefers the order Time → Manner → Place. Memorise the abbreviation: TMP.",
    },
    {
      kind: "table",
      headers: ["Time", "Manner", "Place"],
      rows: [
        ["morgen", "met de fiets", "naar Amsterdam"],
        ["om acht uur", "samen", "in het café"],
        ["elke dag", "snel", "door de stad"],
      ],
    },
    {
      kind: "examples",
      rows: [
        { dutch: "Ik ga morgen met de fiets naar Amsterdam.", english: "I'm going to Amsterdam by bike tomorrow. (T-M-P)" },
        { dutch: "We ontmoeten elkaar om acht uur in het café.", english: "We meet each other at 8 in the café. (Time before Place is OK when there's no manner)" },
      ],
    },
    {
      kind: "callout",
      tone: "tip",
      title: "If you forget TMP",
      text: "Default to placing TIME first (or in slot 1) and PLACE last. The middle is for manner, instrument, or company.",
    },

    { kind: "heading", level: 2, text: "Sentence formulae you can reuse" },
    {
      kind: "paragraph",
      text: "Memorise the skeleton; fill in your own words on exam day. Each formula is one of the three patterns you read about above.",
    },
    {
      kind: "formula",
      title: "Main clause — V2 (verb in slot 2)",
      pattern: "[Slot 1] + [werkwoord] + [rest]",
      legend: [
        { token: "[Slot 1]", meaning: "subject (Ik), or time (Morgen), or place (In Amsterdam) — your choice" },
        { token: "[werkwoord]", meaning: "the conjugated verb — always second" },
      ],
      examples: [
        { dutch: "Ik ga morgen naar school.", english: "Subject in slot 1." },
        { dutch: "Morgen ga ik naar school.", english: "Time in slot 1 — verb stays in slot 2, subject flips." },
        { dutch: "In Amsterdam woont mijn zus.", english: "Place in slot 1." },
      ],
    },
    {
      kind: "formula",
      title: "Main clause — two verbs (sandwich)",
      pattern: "[onderwerp] + [hulpwerkwoord] + [middel] + [hoofdwerkwoord aan het einde]",
      legend: [
        { token: "[hulpwerkwoord]", meaning: "willen, kunnen, moeten, mogen, zullen, gaan, hebben, zijn" },
        { token: "[hoofdwerkwoord]", meaning: "infinitive (gaan, eten) or past participle (gegaan, gegeten)" },
      ],
      examples: [
        { dutch: "Ik wil morgen naar Amsterdam gaan.", english: "I want to go to Amsterdam tomorrow." },
        { dutch: "Hij heeft een boek gekocht.", english: "He bought a book." },
        { dutch: "We kunnen om acht uur de bus nemen.", english: "We can catch the bus at eight." },
      ],
    },
    {
      kind: "formula",
      title: "Yes/no question",
      pattern: "[werkwoord] + [onderwerp] + [rest]?",
      examples: [
        { dutch: "Ga je morgen naar school?", english: "Are you going to school tomorrow?" },
        { dutch: "Heb je een fiets?", english: "Do you have a bike?" },
        { dutch: "Komt hij uit Turkije?", english: "Is he from Turkey?" },
      ],
    },
    {
      kind: "formula",
      title: "WH question",
      pattern: "[vraagwoord] + [werkwoord] + [onderwerp] + [rest]?",
      legend: [
        { token: "[vraagwoord]", meaning: "wie, wat, waar, wanneer, hoe, waarom, hoeveel" },
      ],
      examples: [
        { dutch: "Waar woon je?", english: "Where do you live?" },
        { dutch: "Wanneer kom je thuis?", english: "When do you come home?" },
        { dutch: "Hoeveel broers heb je?", english: "How many brothers do you have?" },
      ],
    },
    {
      kind: "formula",
      title: "Sub-clause (verb at the END)",
      pattern: "[hoofdzin], [voegwoord] + [onderwerp] + [middel] + [werkwoord aan het einde].",
      legend: [
        { token: "[voegwoord]", meaning: "omdat, dat, als, toen, voordat, nadat, hoewel, terwijl, of, zodra" },
      ],
      examples: [
        { dutch: "Ik blijf thuis omdat ik ziek ben.", english: "I stay home because I am sick." },
        { dutch: "Ze leert Nederlands omdat ze hier werkt.", english: "She learns Dutch because she works here." },
        { dutch: "Ik weet niet of hij komt.", english: "I don't know if he is coming." },
      ],
    },
    {
      kind: "formula",
      title: "Time → Manner → Place (TMP) order",
      pattern: "[onderwerp] + [werkwoord] + [tijd] + [manier] + [plaats]",
      examples: [
        { dutch: "Ik ga morgen met de fiets naar Amsterdam.", english: "Time → Manner → Place." },
        { dutch: "We werken elke dag samen op kantoor.", english: "Time → Manner (samen) → Place." },
      ],
    },

    { kind: "heading", level: 2, text: "Quick checklist before the exam" },
    {
      kind: "list",
      items: [
        "Main clause: verb in slot 2. Always. Even when slot 1 is something other than the subject.",
        "Two verbs in a main clause: conjugated one early, the other at the end.",
        "Yes/no question: verb first.",
        "WH-question: question word + verb in slot 2.",
        "After omdat / dat / als / voordat / nadat / terwijl / hoewel / of: verb to the end.",
        "After want, en, maar: keep normal V2 order.",
        "Geen replaces een. Niet usually goes near the end of what it negates.",
        "Order in the middle: Time → Manner → Place.",
      ],
    },
  ],
};
